import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
  useState,
} from 'react';
import {View, ViewStyle} from 'react-native';

export interface WebViewProps {
  source: {html?: string; uri?: string; baseUrl?: string};
  style?: ViewStyle;
  onMessage?: (event: {nativeEvent: {data: string}}) => void;
  onLoad?: () => void;
  onError?: (e: Error) => void;
}

const WebView = forwardRef<any, WebViewProps>((props, ref) => {
  const {source, style, onMessage, onLoad, onError} = props;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(0);

  useImperativeHandle(ref, () => ({
    postMessage: (msg: string) => {
      iframeRef.current?.contentWindow?.postMessage(msg, '*');
    },
    reload: () => {
      if (iframeRef.current?.src) {
        iframeRef.current.src = iframeRef.current.src;
      }
    },
  }));

  useEffect(() => {
    const listener = (e: MessageEvent) => {
      if (iframeRef.current && e.source === iframeRef.current.contentWindow) {
        const {type, height: newHeight} = e.data || {};
        if (type === 'setHeight' && typeof newHeight === 'number') {
          setHeight(newHeight);
        }
        onMessage?.({nativeEvent: {data: e.data}});
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [onMessage]);

  const injectedHeightScript = `
    function sendHeight() {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'setHeight', height }, '*');
    }
    window.addEventListener('load', sendHeight);
    window.addEventListener('resize', sendHeight);
    setTimeout(sendHeight, 100); // in case content renders slowly
  `;

  const finalHtml = source.html
    ? `
      <!DOCTYPE html>
      <html>
        <head>
          <style>body{margin:0;padding:0;}</style>
        </head>
        <body>
          ${source.html}
          <script>${injectedHeightScript}</script>
        </body>
      </html>
    `
    : null;

  return (
    <View style={[style, {height: height || 100}]}>
      <iframe
        ref={iframeRef}
        srcDoc={finalHtml || undefined}
        src={source.uri ?? undefined}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          overflow: 'hidden',
        }}
        onLoad={onLoad}
        onError={
          onError ? () => onError(new Error('iframe load error')) : undefined
        }
        sandbox="allow-scripts allow-same-origin"
      />
    </View>
  );
});

WebView.displayName = 'WebView';
export {WebView};
export default WebView;
